import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  createEstoqueFisico,
  estoqueFisicoKeys,
  fetchEstoquesFisicos,
  updateEstoqueFisico,
} from '@/services/estoqueFisicoService';
import type { IEstoqueFisico } from '@/types/estoqueFisico';
import { readStoredUser } from '@/utils/tenantScope';
import './estoque-fisico.css';

export const Route = createFileRoute('/_authenticated/configuracao/estoque-fisico/')({
  component: EstoqueFisicoPage,
});

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function canWrite(): boolean {
  const role = readStoredUser()?.role;
  return role === 'tenant_admin' || role === 'super_admin' || role === 'operator';
}

function getPaginationPages(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  if (currentPage > 3) pages.push('ellipsis');
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (currentPage < totalPages - 2) pages.push('ellipsis');
  if (totalPages > 1) pages.push(totalPages);
  return pages;
}

function EstoqueFisicoPage() {
  const queryClient = useQueryClient();
  const writeOk = canWrite();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IEstoqueFisico | null>(null);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [codigo, setCodigo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativo, setAtivo] = useState(true);

  const listParams = { page: currentPage, perPage: pageSize };
  const colSpan = writeOk ? 5 : 4;

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: estoqueFisicoKeys.list(listParams),
    queryFn: () => fetchEstoquesFisicos(listParams),
  });

  const items = data?.items ?? [];
  const totalRecords = data?.total ?? 0;
  const totalPages = Math.max(1, data?.totalPages ?? 1);
  const effectivePage = Math.min(currentPage, totalPages);
  const inicio = totalRecords === 0 ? 0 : (effectivePage - 1) * pageSize + 1;
  const fim = Math.min(effectivePage * pageSize, totalRecords);
  const paginationPages = getPaginationPages(effectivePage, totalPages);

  useEffect(() => {
    if (data && data.totalPages > 0 && currentPage > data.totalPages) {
      setCurrentPage(data.totalPages);
    }
  }, [data, currentPage]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: estoqueFisicoKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['entradaProduto', 'estoque'] });
  };

  const createMutation = useMutation({
    mutationFn: createEstoqueFisico,
    onSuccess: () => {
      invalidate();
      closeModal();
      setSuccessMessage('Estoque físico cadastrado.');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao cadastrar.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ codigo: cod, payload }: { codigo: string; payload: { descricao: string; ativo: boolean } }) =>
      updateEstoqueFisico(cod, payload),
    onSuccess: () => {
      invalidate();
      closeModal();
      setSuccessMessage('Estoque físico atualizado.');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao atualizar.')),
  });

  const mutationPending = createMutation.isPending || updateMutation.isPending;

  const openCreate = () => {
    setEditing(null);
    setFormError('');
    setSuccessMessage('');
    setCodigo('');
    setDescricao('');
    setAtivo(true);
    setModalOpen(true);
  };

  const openEdit = (row: IEstoqueFisico) => {
    setEditing(row);
    setFormError('');
    setSuccessMessage('');
    setCodigo(row.codigo);
    setDescricao(row.descricao);
    setAtivo(row.ativo);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError('');
    setCodigo('');
    setDescricao('');
    setAtivo(true);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    const desc = descricao.trim();
    if (!desc) {
      setFormError('Informe a descrição.');
      return;
    }

    if (editing) {
      updateMutation.mutate({
        codigo: editing.codigo,
        payload: { descricao: desc, ativo },
      });
      return;
    }

    const cod = codigo.trim();
    if (!cod) {
      setFormError('Informe código e descrição.');
      return;
    }
    createMutation.mutate({ codigo: cod, descricao: desc, ativo });
  };

  return (
    <div className="estoque-fisico-page">
      <div className="estoque-fisico-page__header">
        <div>
          <p className="estoque-fisico-page__subtitle">
            Armazéns físicos do tenant (cadastro manual e sincronização).
          </p>
        </div>
        {writeOk && (
          <button
            type="button"
            className="estoque-fisico-page__btn-primary"
            disabled={mutationPending}
            onClick={openCreate}
          >
            Novo estoque físico
          </button>
        )}
      </div>

      {successMessage && (
        <p className="estoque-fisico-page__success" role="status">
          {successMessage}
        </p>
      )}

      {isError && (
        <p className="estoque-fisico-page__error" role="alert">
          {extractApiError(error, 'Falha ao carregar estoques físicos.')}
        </p>
      )}

      <div className="estoque-fisico-page__table-container">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Código</Table.Head>
              <Table.Head scope="col">Descrição</Table.Head>
              <Table.Head scope="col">Origem</Table.Head>
              <Table.Head scope="col">Status</Table.Head>
              {writeOk && <Table.Head scope="col">Ações</Table.Head>}
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Carregando…</Table.Cell>
              </Table.Row>
            )}
            {!isLoading && items.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Nenhum estoque físico encontrado.</Table.Cell>
              </Table.Row>
            )}
            {items.map((row) => (
              <Table.Row key={row.codigo}>
                <Table.Cell>{row.codigo}</Table.Cell>
                <Table.Cell>{row.descricao}</Table.Cell>
                <Table.Cell>{row.origem}</Table.Cell>
                <Table.Cell>{row.ativo ? 'Ativo' : 'Inativo'}</Table.Cell>
                {writeOk && (
                  <Table.Cell className="estoque-fisico-page__actions">
                    <button type="button" onClick={() => openEdit(row)}>
                      Editar
                    </button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Content>
        </Table>
      </div>

      {!isLoading && totalRecords > 0 && (
        <div className="estoque-fisico-page__pagination">
          <div className="estoque-fisico-page__pagination-left">
            <label htmlFor="estoque-fisico-page-size">Itens por página:</label>
            <select
              id="estoque-fisico-page-size"
              value={pageSize}
              disabled={isFetching}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <p>
              Mostrando <strong>{inicio.toLocaleString('pt-BR')}</strong> -{' '}
              <strong>{fim.toLocaleString('pt-BR')}</strong> de{' '}
              <strong>{totalRecords.toLocaleString('pt-BR')}</strong> registros
            </p>
          </div>

          <div className="estoque-fisico-page__pagination-controls">
            <button
              type="button"
              className="estoque-fisico-page__page-btn"
              disabled={effectivePage === 1 || isFetching}
              onClick={() => setCurrentPage(effectivePage - 1)}
              aria-label="Página anterior"
            >
              ←
            </button>
            {paginationPages.map((page, index) =>
              page === 'ellipsis' ? (
                <span key={`ellipsis-${index}`} className="estoque-fisico-page__page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={[
                    'estoque-fisico-page__page-btn',
                    page === effectivePage ? 'estoque-fisico-page__page-btn--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setCurrentPage(page)}
                  disabled={isFetching}
                >
                  {page}
                </button>
              ),
            )}
            <button
              type="button"
              className="estoque-fisico-page__page-btn"
              disabled={effectivePage >= totalPages || isFetching}
              onClick={() => setCurrentPage(effectivePage + 1)}
              aria-label="Próxima página"
            >
              →
            </button>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal onClose={closeModal} className="estoque-fisico-page__modal">
          <Modal.Header title={editing ? 'Editar estoque físico' : 'Novo estoque físico'} />
          <Modal.Content>
            <form id="estoque-fisico-form" className="estoque-fisico-page__form" onSubmit={onSubmit}>
              {formError && (
                <p className="estoque-fisico-page__error" role="alert">
                  {formError}
                </p>
              )}
              {editing && (
                <p className="estoque-fisico-page__hint">
                  Origem: <strong>{editing.origem}</strong> (imutável)
                </p>
              )}
              <label htmlFor="ef-codigo">
                Código
                <input
                  id="ef-codigo"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  maxLength={100}
                  required={!editing}
                  disabled={Boolean(editing)}
                  autoFocus={!editing}
                />
              </label>
              <label htmlFor="ef-descricao">
                Descrição
                <input
                  id="ef-descricao"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  maxLength={255}
                  required
                  autoFocus={Boolean(editing)}
                />
              </label>
              <label className="estoque-fisico-page__check" htmlFor="ef-ativo">
                <input
                  id="ef-ativo"
                  type="checkbox"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                />
                Ativo
              </label>
            </form>
          </Modal.Content>
          <Modal.Footer>
            <button
              type="button"
              className="estoque-fisico-page__btn-secondary"
              onClick={closeModal}
              disabled={mutationPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="estoque-fisico-form"
              className="estoque-fisico-page__btn-primary"
              disabled={mutationPending}
            >
              {mutationPending ? 'Salvando…' : editing ? 'Salvar' : 'Cadastrar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
